import { useState, useEffect, useCallback, createContext, useContext, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface SubscriptionData {
  id: string;
  status: string;
  amount: number;
  next_payment_date: string | null;
  mollie_subscription_id: string | null;
}

interface BillingContextType {
  subscription: SubscriptionData | null;
  subscriptionStatus: string;
  trialEndsAt: Date | null;
  isTrialing: boolean;
  isTrialExpired: boolean;
  isActive: boolean;
  isPastDue: boolean;
  isCancelled: boolean;
  cancelledButActive: boolean;
  cancelledDaysLeft: number;
  trialDaysLeft: number;
  loading: boolean;
  refreshBilling: () => Promise<void>;
  startSubscription: () => Promise<string | null>;
  cancelSubscription: () => Promise<boolean>;
}

const BillingContext = createContext<BillingContextType | undefined>(undefined);

export const BillingProvider = ({ children }: { children: ReactNode }) => {
  const { profile } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState("trialing");
  const [trialEndsAt, setTrialEndsAt] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshBilling = useCallback(async () => {
    if (!profile?.organisation_id) return;

    const { data: org } = await supabase
      .from("organisations")
      .select("subscription_status, trial_ends_at")
      .eq("id", profile.organisation_id)
      .single();

    if (org) {
      setSubscriptionStatus(org.subscription_status);
      setTrialEndsAt(new Date(org.trial_ends_at));
    }

    const { data: sub } = await supabase
      .from("subscriptions")
      .select("id, status, amount, next_payment_date, mollie_subscription_id")
      .eq("organisation_id", profile.organisation_id)
      .maybeSingle();

    setSubscription(sub as any);
    setLoading(false);
  }, [profile?.organisation_id]);

  useEffect(() => {
    refreshBilling();
  }, [refreshBilling]);

  const now = new Date();
  const isTrialing = subscriptionStatus === "trialing" && trialEndsAt !== null && trialEndsAt > now;
  const isTrialExpired = subscriptionStatus === "trialing" && trialEndsAt !== null && trialEndsAt <= now;
  const isActive = subscriptionStatus === "active";
  const isPastDue = subscriptionStatus === "past_due";
  const isCancelled = subscriptionStatus === "cancelled" || subscriptionStatus === "failed";
  // Cancelled/failed but still within the paid period (next_payment_date in the future)
  const cancelledButActive = isCancelled && !!subscription?.next_payment_date && new Date(subscription.next_payment_date) > now;
  const cancelledDaysLeft = cancelledButActive && subscription?.next_payment_date
    ? Math.max(0, Math.ceil((new Date(subscription.next_payment_date).getTime() - now.getTime()) / (1000 * 60 * 60 * 24)))
    : 0;
  const trialDaysLeft = trialEndsAt ? Math.max(0, Math.ceil((trialEndsAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))) : 0;

  const startSubscription = async (): Promise<string | null> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return null;

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/mollie-create-subscription`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            "Content-Type": "application/json",
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({
            redirectUrl: `${window.location.origin}/dashboard/settings/billing`,
          }),
        }
      );

      const data = await res.json();
      if (data.checkoutUrl) {
        return data.checkoutUrl;
      }
      return null;
    } catch (err) {
      console.error("Failed to start subscription:", err);
      return null;
    }
  };

  const cancelSubscription = async (): Promise<boolean> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return false;

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/mollie-cancel-subscription`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            "Content-Type": "application/json",
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
        }
      );

      const data = await res.json();
      if (data.success) {
        await refreshBilling();
        return true;
      }
      return false;
    } catch (err) {
      console.error("Failed to cancel subscription:", err);
      return false;
    }
  };

  return (
    <BillingContext.Provider
      value={{
        subscription,
        subscriptionStatus,
        trialEndsAt,
        isTrialing,
        isTrialExpired,
        isActive,
        isPastDue,
        isCancelled,
        cancelledButActive,
        cancelledDaysLeft,
        trialDaysLeft,
        loading,
        refreshBilling,
        startSubscription,
        cancelSubscription,
      }}
    >
      {children}
    </BillingContext.Provider>
  );
};

export const useBilling = () => {
  const ctx = useContext(BillingContext);
  if (!ctx) throw new Error("useBilling must be used within BillingProvider");
  return ctx;
};
